'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface UseCase {
  id: string;
  title: string;
  icon: string;
  description: string;
  features: string[];
  stat: {
    label: string;
    value: string;
  };
}

const useCases: UseCase[] = [
  {
    id: 'residential',
    title: 'Residential',
    icon: 'HomeIcon',
    description:
      'Perfect for residential HVAC contractors handling AC replacements, furnace installations, and routine maintenance quotes.',
    features: [
      'Residential equipment pricing database',
      'Permit cost calculations by county',
      'Seasonal pricing adjustments',
      'Financing options integration',
      'Warranty package builder',
    ],
    stat: {
      label: 'Average Quote Time',
      value: '2.5 min',
    },
  },
  {
    id: 'commercial',
    title: 'Commercial',
    icon: 'BuildingOfficeIcon',
    description:
      'Designed for commercial HVAC projects including rooftop units, chillers, and large-scale installations.',
    features: [
      'Commercial equipment specifications',
      'Multi-zone system pricing',
      'Labor multiplier for large teams',
      'Project timeline estimator',
      'Bid package generation',
    ],
    stat: {
      label: 'Project Accuracy',
      value: '97%',
    },
  },
  {
    id: 'emergency',
    title: 'Emergency Services',
    icon: 'BoltIcon',
    description:
      'Optimized for emergency calls and after-hours service with rush pricing and immediate quote generation.',
    features: [
      'After-hours pricing multipliers',
      'Emergency parts availability check',
      'Rush service premium calculator',
      'On-site quote generation',
      'SMS quote delivery',
    ],
    stat: {
      label: 'Response Time',
      value: '< 90 sec',
    },
  },
];

export default function UseCaseTabs() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="section-padding bg-white border-y border-border">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Built for <span className="text-primary">Every Use Case</span>
          </h2>
          <p className="text-xl text-muted-foreground">
            Tailored features for residential, commercial, and emergency services
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {useCases.map((useCase, index) => (
            <button
              key={useCase.id}
              onClick={() => setActiveTab(index)}
              className={`flex items-center gap-3 px-6 py-3 rounded-full font-semibold transition-all ${
                activeTab === index
                  ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              <Icon
                name={useCase.icon as any}
                size={20}
                variant={activeTab === index ? 'solid' : 'outline'}
              />
              {useCase.title}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="max-w-4xl mx-auto">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.id}
              className={`transition-all duration-500 ${
                activeTab === index
                  ? 'opacity-100 translate-y-0' :'opacity-0 absolute pointer-events-none -translate-y-4'
              }`}
            >
              <div className="bg-card border border-border rounded-2xl p-8 md:p-12 shadow-xl">
                <div className="flex items-start gap-6 mb-8">
                  <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <Icon
                      name={useCase.icon as any}
                      size={32}
                      variant="outline"
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <h3 className="text-3xl font-heading font-bold text-foreground mb-3">
                      {useCase.title} HVAC
                    </h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {useCase.description}
                    </p>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-sm uppercase tracking-widest font-semibold text-muted-foreground mb-4">
                    Key Features
                  </h4>
                  <ul className="grid md:grid-cols-2 gap-4">
                    {useCase.features.map((feature, featureIndex) => (
                      <li
                        key={`${useCase.id}_feature_${featureIndex}`}
                        className="flex items-start gap-3"
                      >
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          variant="solid"
                          className="text-success flex-shrink-0 mt-0.5"
                        />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6 border-t border-border">
                  <div className="inline-flex items-center gap-8 bg-muted/50 rounded-xl px-6 py-4">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                        {useCase.stat.label}
                      </div>
                      <div className="text-3xl font-mono-numbers font-bold text-primary">
                        {useCase.stat.value}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}